import React, { useEffect, useLayoutEffect, useState } from 'react';
import { trackPageView } from '@tourlane/tracking';
import useZoomVideo from './useZoomVideo/useZoomVideo';
import ZoomConnectingScreen from './ZoomConnectingScreen';
import GoodbyeScreen from '../GoodbyeScreen/GoodbyeScreen';
import IntroContainer from '../IntroContainer/IntroContainer';
import { useParams } from 'react-router-dom';
import { useAppState } from '../../state';
import { captureMessage } from '../../utils/sentry';
import { checkMediaPermissionState } from '../../utils/checkMediaPermissionState';
import { getZoomEntryId } from '../../utils/zoomConfig';

export default function ZoomFlow() {
  const { travelRequestUuid } = useParams<{ travelRequestUuid: string }>();
  const { user, call } = useAppState();
  const [retryCount, setRetryCount] = useState(0);

  useLayoutEffect(() => {
    // these things are read by the Zoom SDK and can then be read in the Zoom contact center flow.
    // See Zoom contact center > Preferences > Variables
    if (travelRequestUuid) localStorage.setItem('lead_uuid', travelRequestUuid);
    if (user?.displayName) localStorage.setItem('customer_name', user.displayName);
  }, [travelRequestUuid, user?.displayName]);

  const entryId = getZoomEntryId(call?.schedulingMode);
  const { status, error, start, restart } = useZoomVideo(entryId);

  useEffect(() => {
    if (status === 'sdk-ready') {
      start();
    }
  }, [status, start]);

  useEffect(() => {
    if (status === 'active' && process.env.REACT_APP_DEPLOYED_TO) {
      trackPageView({ pageType: 'video-chat-room' });
    }
  }, [status]);

  const handleRetry = () => {
    // Retry immediately — this is the recovery path, so it must feel instant and
    // never wait on anything. restart() resets the hook and re-drives load →
    // sdk-ready, which re-fires the start() effect above. (Previously this was a
    // key-based remount of an inner component; now it's an explicit hook API.)
    setRetryCount((prev) => prev + 1);
    restart();

    // Fire-and-forget diagnostic: record (for Sentry only) whether the retry is
    // happening against a still-denied permission state. Deliberately not awaited
    // so a slow/unusual Permissions API can't delay the retry above. Defensive
    // .catch so this non-awaited call can never leak an unhandled rejection.
    checkMediaPermissionState()
      .then((state) => {
        if (state && (state.camera === 'denied' || state.microphone === 'denied')) {
          captureMessage('Retry attempted while camera/microphone permission still denied', 'camera_microphone_permission', {
            camera_state: state.camera,
            microphone_state: state.microphone,
          });
        }
      })
      .catch(() => {});
  };

  if (status === 'ended') {
    return <GoodbyeScreen />;
  }

  if (status === 'active') {
    return null;
  }

  return (
    <IntroContainer>
      <ZoomConnectingScreen error={error} retryExhausted={retryCount >= 1} onRetry={handleRetry} />
    </IntroContainer>
  );
}
